import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useCollection } from '@nandorojo/swr-firestore';
import {
  IPhotoCard,
  IphotocardCharList,
  IselectPhotocardList,
} from '@typings/photocard/photocard.interface';
import Color from '@styles/Color';

import Label from '@components/dataEntry/Input/Label';
import keyCode from '@typings/setting/keyCode.enum';
import DropdownWrapper, {
  dropdownDirMap,
} from '@components/wrapper/DropdownWrapper';
import { AnimatePresence } from 'framer-motion';
import ResultDropdown from '@components/dataEntry/Input/TagInput/ResultDropdown';
import Avatar from '@components/dataDisplay/Avatar';
import { CollectionPath } from '@typings/collections.enum';
import { useRecoilValue } from 'recoil';
import { boardSetListState } from '@atom/setting/boardSetListState';
import { BOARD_TYPE } from '@typings/boardType';

const focusInput = css`
  /* border: 1px solid ${Color.GRAY_8}; */
`;

const Container = styled.div<{
  disabled?: boolean;
  block?: boolean;
  isFocus?: boolean;
}>`
  position: relative;
  min-height: 40px;
  height: auto;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px 8px;
  border-radius: var(--cardBorderRadius);
  outline: none;
  background-color: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);

  ${({ isFocus }) => isFocus && focusInput}

  gap: 8px;

  &:hover {
    border: 1px solid var(--primaryColor);
  }
`;

export const Tag = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 14px;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }
`;

// 태그 검색
function handleFilterLists(target, allList) {
  const filterResultList = allList!.filter((compareItem) => {
    const { value: originValue } = compareItem;
    const compareTarget = target.toLowerCase().replace(/\s+/g, '');
    const compareOrigin = originValue.toLowerCase().replace(/\s+/g, '');
    return compareOrigin.includes(compareTarget);
  });
  return filterResultList;
}

type Props = {
  label?: string;
  initialList: IphotocardCharList[] | undefined | null;
  setUserList: any;
  setUserData?: any;
  goDirect?: boolean;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  maxLength?: number;
  onlySelect?: boolean;
  disabled?: boolean;
  placeholder?: string;
  block?: boolean;
  size?: 'sm' | 'md';
  dir?: keyof typeof dropdownDirMap | undefined;
};

// -----------------------------------

// initial 값은 ->  [{key:'id1', path:'photocard2' }] 과 같음. IphotocardCharList[]
// 이 값을 selectedList = [ {key:id, thumbnail:'', value: ''}...] 와 같이 변형시킬 것임.

// 이미 검색값을 위해 allPhotocardList가 준비되어 있음.
// 해당 allList에서 initialList의 key로 순회함.
// search기능을 통해 filter되는 데이터는 searchResultList로 따로임.

// -----------------------------------

function SelectPhotocard({
  label,
  initialList,
  setUserList,
  setUserData,
  goDirect = true,
  defaultValue,
  onChange,
  maxLength,
  onlySelect,
  disabled,
  placeholder,
  block,
  size = 'md',
  dir = 'topLeft',
}: Props) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const handleFocus = useCallback(() => setIsFocus(true), []);
  const handleBlur = useCallback(() => setIsFocus(false), []);
  const [keyword, setkeyowrd] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // 직접 데이터의 input / output은 -> userList, setUserList가 담당

  // 내부에서 움직이는 값
  const [selectedList, setSelectedList] = useState<IselectPhotocardList[]>([]);

  // 모든 유저 리스트
  const [allPhotocardList, setAllPhotocardList] = useState<any[]>([]);

  // 서치 결과값이 오는 list
  const [searchResultList, setSearchResultList] = useState<any[] | null>(
    allPhotocardList || [],
  );

  // 📍 설치된 모든 포토카드형 게시판을 동적으로 감지 (photocard, photocard2, ...)
  const boardSetList = useRecoilValue(boardSetListState);
  const photocardPaths = useMemo(
    () =>
      (boardSetList || [])
        .filter((b) => b.typeName === BOARD_TYPE.PHOTOCARD)
        .map((b) => b.path),
    [boardSetList],
  );

  // 각 포토카드 게시판의 데이터를 동적으로 로드
  const { data: list1 } = useCollection<IPhotoCard>(
    photocardPaths[0] || CollectionPath.PHOTOCARD,
    { listen: true },
  );
  const { data: list2 } = useCollection<IPhotoCard>(
    photocardPaths.length > 1 ? photocardPaths[1]! : null,
    { listen: true },
  );
  const { data: list3 } = useCollection<IPhotoCard>(
    photocardPaths.length > 2 ? photocardPaths[2]! : null,
    { listen: true },
  );

  // -----------------------------------------------------------
  //
  useEffect(() => {
    if (!allPhotocardList || !initialList) return;
    const initialKeyArray = initialList.map((item) => item?.key);
    const selectedPhotocards = allPhotocardList.filter(
      (item) => item.key && initialKeyArray.includes(item.key),
    );
    if (setUserData) setUserData(selectedPhotocards); //컴포넌트 외부에서 selected data를 필요로 할때 사용
    setSelectedList(selectedPhotocards); // 컴포넌트 내부에서만 작동하는 데이터 state
  }, [initialList, allPhotocardList]);
  // -----------------------------------------------------------

  // -----------------------------------------------------------
  // 모든 포토카드 데이터 값을 불러옴
  useEffect(() => {
    if (allPhotocardList) {
      setSearchResultList(allPhotocardList);
      const result = handleFilterLists(keyword, allPhotocardList);
      if (keyword.length > 0) {
        handleFocus();
        setSearchResultList(result || allPhotocardList);
      }
      if (!keyword) setSearchResultList(allPhotocardList);
      if (result.length === 0) handleBlur();
    }
  }, [keyword, allPhotocardList]);

  // 📍 모든 포토카드 게시판의 데이터를 병합 (동적)
  useEffect(() => {
    const allLists: [any[] | null | undefined, string][] = [
      [list1, photocardPaths[0] || CollectionPath.PHOTOCARD],
      [list2, photocardPaths[1] || ''],
      [list3, photocardPaths[2] || ''],
    ];

    const rebaseList = allLists.flatMap(([list, path]) =>
      (list || []).map((item) => ({
        key: item.id,
        value: item.title || '',
        thumbnail: item.thumbnail || '',
        path: item.path || path,
      })),
    );

    setAllPhotocardList(rebaseList);
  }, [list1, list2, list3, photocardPaths]);

  // -----------------------------------------------------------

  // -----------------------------------------------------------
  // 서치 결과값을 반영함
  useEffect(() => {
    if (searchResultList && keyword) {
      const findIndex = searchResultList?.findIndex(
        (list) => list.value === keyword,
      );
      setActiveIndex(findIndex);
    }
  }, [searchResultList, keyword]);
  // -----------------------------------------------------------

  //인풋 value와 기존 input에 있는 유저 중복 여부
  const isDuplicateValue = (targetValue: string): boolean => {
    if (!selectedList) return false;
    const result = selectedList.filter((item) => item.value === targetValue);
    return result.length !== 0;
  };

  const handleAddUser = useCallback(
    (user) => {
      if (onlySelect) {
        setUserList([user]);
        setkeyowrd('');
      } else {
        setUserList([...selectedList!, user]);
        setkeyowrd(''); //reset
      }
    },
    [onlySelect, selectedList],
  );

  const handleChange = useCallback(
    (e: any) => {
      if (onChange) onChange(e);
      setkeyowrd(e.target.value);
    },
    [onChange],
  );

  const handleKeyPress = useCallback(
    (e) => {
      e.stopPropagation();
      let selectedIndex = activeIndex;
      if (e.key === keyCode.ENTER && allPhotocardList) {
        const targetValue = e.target.value;
        // 기존의 allUser와 비교
        const matchPrevUser = allPhotocardList.filter(
          (item) => item.value === targetValue,
        );
        if (targetValue && matchPrevUser.length === 0) {
          selectedIndex = activeIndex === 0 ? 0 : activeIndex;
          const selectedValue = searchResultList![selectedIndex].value;

          if (!isDuplicateValue(selectedValue)) {
            handleAddUser(searchResultList![selectedIndex]);
          }
        } else {
          handleSelect();
        }
      }
    },
    [selectedList, allPhotocardList, searchResultList, activeIndex],
  );

  // enter 또는 click 했을 때 적용됨
  const handleSelect = useCallback(
    (index?: number) => {
      let selectedIndex = activeIndex;
      if (!isDuplicateValue(keyword) && searchResultList!.length === 1) {
        handleAddUser(searchResultList![0]);
      } else {
        if (!index) {
          // console.log('키보드로');
          selectedIndex = activeIndex === 0 ? 0 : activeIndex;
          const selectedValue = searchResultList![selectedIndex].value;

          if (!isDuplicateValue(selectedValue)) {
            handleAddUser(searchResultList![selectedIndex]);
          }
        }
        if (index) {
          selectedIndex = index === 0 ? 0 : index;
          const selectedValue = searchResultList![selectedIndex].value;
          // console.log('클릭으로');

          if (!isDuplicateValue(selectedValue)) {
            handleAddUser(searchResultList![selectedIndex]);
          }
        }
      }
    },
    [activeIndex, searchResultList, selectedList, keyword],
  );

  function HandleRemoveKeyword(index: number) {
    const copyList = [...selectedList!];
    copyList.splice(index, 1);
    setUserList([...copyList]);
    setSelectedList([...copyList]);
  }

  const handleKeyDown = (e: any) => {
    if (e.key === keyCode.BACKSPACE && !keyword) {
      HandleRemoveKeyword(selectedList ? selectedList.length - 1 : 0);
    }
  };

  // 드롭다운 바깥 클릭
  function handleClickOutside(e: any, dropdownNode) {
    if (targetRef.current && isFocus && dropdownNode) {
      const isClickOutside =
        !targetRef.current.contains(e.target) &&
        !dropdownNode.contains(e.target);
      if (isClickOutside) handleBlur();
    }
  }

  if (!selectedList) return null;

  return (
    <>
      {label && <Label size={size}>{label}</Label>}
      <Container
        disabled={disabled}
        isFocus={isFocus}
        ref={targetRef}
        style={{
          width: block ? '100%' : 'auto',
          minHeight: size === 'sm' ? '20px' : '40px',
        }}
      >
        {selectedList.map((list, i) => {
          return (
            <Tag key={i} onClick={() => HandleRemoveKeyword(i)}>
              <Avatar size={20} photoURL={list.thumbnail} />
              <span>{list.value}</span>
            </Tag>
          );
        })}
        {onlySelect && selectedList.length < 1 && (
          <input
            type="text"
            maxLength={maxLength}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={keyword}
            onChange={handleChange}
            placeholder={placeholder || '캐릭터 이름 검색'}
            onKeyPress={(e) => handleKeyPress(e)}
            onKeyDown={(e) => handleKeyDown(e)}
            style={{ height: 20, width: '100%' }}
          />
        )}
        <AnimatePresence>
          {isFocus && allPhotocardList && (
            <DropdownWrapper
              open={isFocus}
              direction={dir}
              handleClickOutside={handleClickOutside}
              style={{ width: '100%' }}
              maxHeight={240}
            >
              <ResultDropdown
                isOpen={isFocus}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                filteredList={searchResultList}
                handleSelect={handleSelect}
                maxHeight={240}
              />
            </DropdownWrapper>
          )}
        </AnimatePresence>
      </Container>
    </>
  );
}

export default SelectPhotocard;
